'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import { Close } from '@mui/icons-material'
import LoginIcon from '@mui/icons-material/Login'
import { Box, IconButton, Stack, useMediaQuery } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { captureException } from '@sentry/nextjs'
import axios from 'axios'
import { addDoc, collection, doc, getDoc } from 'firebase/firestore'
import { get } from 'lodash'
import moment from 'moment-timezone'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import posthog from 'posthog-js'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import CustomTextField from 'src/components/mui/CustomInput'
import PhoneInput from 'src/components/mui/PhoneInput'
import CustomTypo, { CustomModalTypo } from 'src/components/mui/StyledTypo'
import { useDialogData } from 'src/context/DialogContext'
import { db } from 'src/utils/firebase'
import { getCountry, getLocation } from 'src/utils/location'
import { sendSlackMessageToChannel } from 'src/utils/sendMessageToSlackChannel'
import * as yup from 'yup'
import LoopLogo from '../assets/logos/loop_logo_short.svg'

export interface DialogFormValues {
  email: string
  name: string
  organization: string
  phone: string
  referral_source: string
}

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Email format is invalid')
    .required('Email is required'),
  name: yup
    .string()
    .min(3, 'Minimum 3 characters are required')
    .required('Name is required'),
  organization: yup.string().required('Organization is required'),
  phone: yup
    .string()
    .min(14, 'Minimum 10 numbers are required')
    .required('Phone is required'),
  referral_source: yup.string().required('Details are required')
})

interface PropTypes {
  small?: boolean
  onClose?: () => void
  fromAprio?: boolean
}

export default function Section6(props: PropTypes) {
  const theme = useTheme()
  const { closeDialog } = useDialogData()
  const router = useRouter()

  const [loading, setLoading] = useState(false)
  const sm = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty }
  } = useForm<DialogFormValues>({
    defaultValues: {
      email: '',
      name: '',
      organization: '',
      phone: '',
      referral_source: ''
    },
    resolver: yupResolver(validationSchema),
    mode: 'all'
  })

  async function onSubmit(values: DialogFormValues) {
    try {
      setLoading(true)
      const { name, email, organization, phone, referral_source } = values
      const TriggerPage = 'Landing Page'
      const pageName = window.location.href
      const redirectionUrl = localStorage.getItem('referrer') ?? 'Direct'

      let emailBody = {}

      const location = await getLocation()
      const date_time = moment()
        .tz('America/Los_Angeles')
        .format('MMMM Do YYYY, h:mm:ss a z')

      if (props.fromAprio) {
        emailBody = {
          template_name: 'new-signup',
          to_emails: [
            'aimee.gartner@aprio.com',
            'frank.mclaughlin@aprio.com',
            'ak@tryloop.ai'
          ],
          extra_data: {
            email,
            aprio: true,
            name,
            phone,
            organization,
            url: pageName,
            location: location,
            date_time: date_time
          }
        }
      } else {
        const emailsDoc = await getDoc(
          doc(db, 'email_alerts', 'inbound_subscribers')
        )
        const emails = get(emailsDoc.data(), 'emails', [])

        emailBody = {
          template_name: 'new-signup',
          to_emails: emails,
          from_email: 'welcome@setup.tryloop.ai',
          extra_data: {
            name,
            email,
            organization,
            phone,
            referral_source: referral_source,
            source: TriggerPage,
            url: pageName,
            email_subject: 'Urgent - New Inbound Lead',
            location: location,
            date_time: date_time,
            redirection_url: redirectionUrl
          }
        }
      }

      await addDoc(collection(db, 'landing-page-visitors'), {
        ...emailBody,
        organization,
        phone,
        source: TriggerPage,
        url: pageName,
        created_at: new Date().toISOString()
      })

      const country = await getCountry()
      if (country !== 'India') {
        await sendSlackMessageToChannel({
          title: `Details: \t
          Name: \`${name}\` \t
          Email: \`${email}\` \t
          Org: \`${organization}\` \t
          Phone: \`${phone}\` \t
          Location: \`${location}\` \t
          Referral Source: \`${referral_source}\` \t
          Redirection URL: \`${redirectionUrl}\` \t
          Source: \`${TriggerPage}\` \t
          Source URL: \`${pageName}\``,
          message: 'New Inbound Lead',
          channel: '#new-inbound-leads'
        })

        await axios.post(
          `https://api.loopapplication.xyz/notification/email_digest`,
          emailBody
        )
      }

      posthog.capture('Landing Leads', {
        name,
        email,
        organization,
        phone,
        referral_source,
        TriggerPage,
        pageName
      })

      closeDialog()
      router.push(`/schedule?name=${name}&email=${email}`)
    } catch (error) {
      captureException(error)
    }
    setLoading(false)
  }

  return (
    <Box
      bgcolor="white"
      padding={sm ? 2 : 4}
      position="relative"
      sx={{
        maxWidth: 'fit-content',
        minWidth: { xs: '100%', md: '700px' },
        height: {
          xs: '100vh',
          md: 'auto'
        },
        overflowY: 'auto'
      }}>
      <Stack direction={'row'}>
        {props.small && (
          <IconButton
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              color: 'black'
            }}
            onClick={props.onClose}>
            <Close color="inherit" />
          </IconButton>
        )}
        <Stack direction={'row'} width={'100%'} gap={5}>
          <Box
            sx={{
              flex: 2,
              height: '100%',
              bgcolor: 'rgb(18, 41, 55)',
              borderRadius: 1,
              p: 1,
              display: { xs: 'none', md: 'flex' }
            }}>
            <Stack
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '100%',
                background: `url('/background_signup.png')`,

                py: 5,
                px: 2
              }}>
              <Box></Box>
              <CustomModalTypo c={'#FFFFFF'}>
                Trusted by{' '}
                <span
                  style={{
                    fontFamily: 'Sora',
                    color: '#55C4FF'
                  }}>
                  4000+
                </span>{' '}
                locations across USA
              </CustomModalTypo>
            </Stack>
          </Box>
          <Box
            sx={{
              flex: 3,
              py: 2,
              px: { xs: 2, sm: 5, md: 2 }
            }}>
            <Box
              sx={{
                height: '100%'
              }}>
              <Box>
                <Image src={LoopLogo} alt="logo" width={30} height={30} />
              </Box>
              <Stack pt={'30px'}>
                <CustomModalTypo
                  ta={'left'}
                  fs={'32px'}
                  m={'0px 0'}
                  lh={'132%'}
                  fw={600}
                  w={'100%'}>
                  Welcome to Loop
                </CustomModalTypo>
                <CustomTypo
                  color={alpha('#122937', 0.5)}
                  sx={{
                    fontSize: {
                      xs: 16,
                      sm: 16,
                      md: 14
                    },
                    lineHeight: '132%',
                    fontWeight: 400,
                    fontFamily: 'Sora',
                    mb: {
                      xs: 2,
                      md: 3
                    },
                    ml: 0.5,
                    textAlign: {
                      xs: 'left'
                    }
                  }}>
                  Fill out the form below and we’ll get back to you soon.
                </CustomTypo>
                <Stack gap={2}>
                  <CustomTextField
                    fullWidth
                    name="email"
                    type="email"
                    control={control}
                    label="Email*"
                    placeholder="Enter your email"
                    errors={errors}
                  />
                  <CustomTextField
                    fullWidth
                    name="name"
                    control={control}
                    label="Name*"
                    placeholder="Enter your name"
                    errors={errors}
                  />
                  <CustomTextField
                    fullWidth
                    name="organization"
                    control={control}
                    label="Company name*"
                    placeholder="Enter your company name"
                    errors={errors}
                  />
                  <PhoneInput
                    fullWidth
                    name="phone"
                    control={control}
                    label="Phone number*"
                    placeholder="Enter your phone number"
                    errors={errors}
                  />
                  {!props.fromAprio && (
                    <CustomTextField
                      fullWidth
                      name="referral_source"
                      control={control}
                      label="How did you hear about us?*"
                      placeholder="Enter details"
                      errors={errors}
                    />
                  )}

                  <button
                    onClick={handleSubmit(onSubmit)}
                    disabled={!isDirty || !isValid || loading}
                    style={{
                      width: '100%',
                      borderRadius: 2,
                      marginTop: '10px'
                    }}
                    className={`${
                      props.small ? 'p-2' : 'p-4'
                    } bg-[#186174] text-white text-center disabled:opacity-50 disabled:cursor-not-allowed mb-2`}>
                    <LoginIcon />
                    <span className="text-white ml-2">Submit</span>
                  </button>
                </Stack>
                <Stack
                  direction={'row'}
                  justifyContent={'center'}
                  alignItems={'center'}>
                  <CustomModalTypo
                    c={'#777777'}
                    fw={200}
                    fs={12}
                    w={'fit-content'}
                    m={'0px'}>
                    Already have an account?{' '}
                  </CustomModalTypo>
                  <Link href={`${process.env.NEXT_PUBLIC_LOOP_APP_URL}`}>
                    <span className="text-[#186174] text-[12px] pl-1">
                      Log In
                    </span>
                  </Link>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
}
